<script setup>
import { ref, defineProps, defineEmits, computed, onMounted, watch } from 'vue';
import { useAppStore } from '../store/app';
import Datepicker from '@vuepic/vue-datepicker';
import '@vuepic/vue-datepicker/dist/main.css';
import { rules } from '../common/formRules';
import { addWeeks, addDays } from 'date-fns';
import { PAYMENT_TYPES, CREDITCARD_PAYMENT_TYPE_VALUE } from '../common/constants';

const props = defineProps({
  showDialog: {
    type: Boolean,
  },
  product: {
    type: Object,
  },
  data: {
    type: Object,
  },
  callback: {
    type: Function,
  },
});

const store = useAppStore();

onMounted(() => {
  editableSubscription.value.customer = { ...props.data.customer };
  editableSubscription.value.isNew = props.data.isNew;
  if (props.product?.id) {
    editableSubscription.value.product = { ...props.product };
  } else {
    editableSubscription.value.product = { ...store.products[0] };
  }
  if (editableSubscription.value.isNew) {
    editableSubscription.value.customer.subscriptionStartDate = addDays(
      new Date(),
      editableSubscription.value.product?.freeTrialAvailable
        ? editableSubscription.value.product?.daysFreeTrial
        : 0,
    );
    // set default exp as two weeks
    editableSubscription.value.customer.expirationDate = addWeeks(
      addDays(
        new Date(),
        editableSubscription.value.product?.freeTrialAvailable
          ? editableSubscription.value.product?.daysFreeTrial
          : 0,
      ),
      2,
    );
  }
});

const emit = defineEmits(['onClose']);
const duplicateSubError = ref('');
const editableSubscription = ref({
  product: {},
  customer: {},
  isCommercial: true,
  subscriptionLength: 2,
  paymentType: CREDITCARD_PAYMENT_TYPE_VALUE,
  date: new Date(),
});

const productSubLevels = computed(() => {
  return store.subscriptionLevels.filter(
    (lv) => lv.productId === editableSubscription.value.product.id,
  );
});

const subscriptionLengths = [
  { value: 2, label: '2 weeks' },
  { value: 4, label: '4 weeks' },
  { value: 8, label: '8 weeks' },
];

const dateFormat = (date) => {
  return date.toLocaleDateString();
};
const searchingCustomer = ref(false);
const foundExistingCustomer = ref(false);

/* wa
const onEmailChange = (emailValue) => {
  foundExistingCustomer.value = false;
  if (emailValue) {
    searchingCustomer.value = true;
    const foundCustomer = store.customersByProduct.find((c) => c.email === emailValue;
    if (foundCustomer) {
      editableSubscription.value.customer = { ...foundCustomer };
      foundExistingCustomer.value = true;
    }
    editableSubscription.value.customer.email = emailValue;
  }
  searchingCustomer.value = false;
};
*/
const newSubRef = ref(null);
const onSaveSubscription = async () => {
  store.appLoading = true;
  duplicateSubError.value = '';
  newSubRef.value.resetValidation();
  const validateResult = await newSubRef.value.validate();
  // Check Validation
  if (!validateResult.valid) {
    store.appLoading = false;
    return;
  }
  let res = {};
  if (editableSubscription.value.isNew) {
    await store.getCustomerSubscriptions({
      customer: {
        email: editableSubscription.value.customer.email,
        username: editableSubscription.value.customer.username,
      },
    });
    if (
      store.customerSubscriptions.findIndex(
        (s) => s.status === 1 && s.productId === editableSubscription.value.product.id,
      ) != -1
    ) {
      duplicateSubError.value = 'Customer already has an active subscription for this product';
      store.appLoading = false;
      return;
    }
    res = await store.createNewSubscription({ ...editableSubscription.value });
  } else {
    res = await store.updateSubscription({ ...editableSubscription.value.customer });
  }
  props.callback({ customer: props.data.customer });
  store.appLoading = false;
  emit('onClose', res);
};

const onSubscriptionLengthChange = (value) => {
  // Add weeks to subscription start date, not to current date
  const startDate = editableSubscription.value.customer.subscriptionStartDate || new Date();
  editableSubscription.value.customer.expirationDate = addWeeks(startDate, value);
};

const onProductSelectChange = () => {
  editableSubscription.value.customer.subscriptionStartDate = addDays(
    new Date(),
    editableSubscription.value.product?.freeTrialAvailable
      ? editableSubscription.value.product?.daysFreeTrial
      : 0,
  );
  editableSubscription.value.customer.subscriptionLevelId = productSubLevels.value[0].id;
  duplicateSubError.value = '';
};

watch(
  () => editableSubscription.value.customer.paymentType,
  (value) => {
    if (['invoiced', 'staff'].includes(value)) {
      editableSubscription.value.customer.expirationDate = new Date('2040-12-31');
    }
  },
);

// Restart EWAY Rebill functionality removed — EWAY SOAP API cannot create new rebills with masked CC data
</script>
<template>
  <v-dialog persistent :model-value="true" width="600">
    <v-card>
      <v-card-title>
        <v-row class="d-flex ma-4">
          <v-col class="text-center">{{
            editableSubscription.isNew ? 'New Subscription' : 'Edit Subscription'
          }}</v-col>
        </v-row>
      </v-card-title>
      <v-card-text>
        <v-dialog v-model="store.appLoading" class="align-center" persistent>
          <div class="text-center">
            <v-progress-circular
              :size="100"
              width="10"
              color="primary"
              indeterminate></v-progress-circular>
            <div class="text-h6 text-white">Processing..</div>
          </div>
        </v-dialog>
        <v-form ref="newSubRef">
          <!--<v-row>
            <v-col>
              <v-switch
                color="primary"
                :model-value="true"
                label="Existing Customer"
                inset></v-switch>
            </v-col>
          </v-row>-->
          <v-row>
            <v-col cols="6">
              <v-select
                label="Product"
                return-object
                :readonly="!editableSubscription.isNew || props.product?.id"
                :items="store.products"
                variant="outlined"
                density="compact"
                item-title="name"
                v-model="editableSubscription.product"
                @update:model-value="onProductSelectChange">
              </v-select>
            </v-col>
            <v-col cols="6">
              <v-select
                label="Subscription Level"
                :items="productSubLevels"
                :rules="[rules.required]"
                variant="outlined"
                density="compact"
                item-title="name"
                item-value="id"
                v-model="editableSubscription.customer.subscriptionLevelId">
              </v-select
            ></v-col>
          </v-row>
          <v-row>
            <v-col cols="6">
              <v-text-field
                variant="outlined"
                v-model="editableSubscription.customer.username"
                label="User Name"
                required
                density="compact"
                persistent-placeholder
                :readonly="!editableSubscription.isNew"
                :rules="[rules.required]"
                :error-messages="duplicateSubError"
                @update:model-value="duplicateSubError = ''">
              </v-text-field>
            </v-col>
            <v-col cols="6">
              <v-text-field
                variant="outlined"
                v-model="editableSubscription.customer.email"
                label="Email"
                required
                density="compact"
                persistent-placeholder
                :rules="[rules.required]"
                :readonly="!editableSubscription.isNew"
                @update:model-value="duplicateSubError = ''">
                <template v-slot:loader>
                  <v-progress-linear
                    :active="searchingCustomer"
                    color="success"
                    absolute
                    height="4"
                    indeterminate></v-progress-linear>
                </template>
                <v-tooltip
                  v-if="foundExistingCustomer"
                  model-value="true"
                  @update:model-value="() => {}"
                  color="warning"
                  activator="parent"
                  location="bottom"
                  >Customer already has an active subscription</v-tooltip
                >
              </v-text-field>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="6">
              <v-text-field
                variant="outlined"
                v-model="editableSubscription.customer.firstName"
                label="First Name"
                required
                :readonly="!editableSubscription.isNew"
                density="compact"
                persistent-placeholder></v-text-field>
            </v-col>
            <v-col cols="6">
              <v-text-field
                variant="outlined"
                v-model="editableSubscription.customer.lastName"
                label="Last Name"
                required
                :readonly="!editableSubscription.isNew"
                persistent-placeholder
                density="compact"></v-text-field>
            </v-col>
          </v-row>
          <v-row>
            <v-col>
              <div>Payment Type:</div>
              <v-select
                :items="PAYMENT_TYPES"
                variant="outlined"
                density="compact"
                item-title="label"
                item-value="value"
                hide-details
                v-model="editableSubscription.customer.paymentType">
              </v-select>
            </v-col>
            <v-col>
              <div>
                Start Date: <v-icon v-if="editableSubscription.isNew">mdi-information </v-icon>
                <v-tooltip v-if="editableSubscription.isNew" activator="parent" location="bottom">{{
                  `start date is auto set based on ${editableSubscription.product.daysFreeTrial} days free trial for product ${editableSubscription.product.name}`
                }}</v-tooltip>
              </div>
              <Datepicker
                v-model="editableSubscription.customer.subscriptionStartDate"
                :enable-time-picker="false"
                auto-apply
                readonly
                placeholder="Start Date"
                :format="dateFormat"
                :min-date="new Date()"
                :teleport="true"
                :clearable="false">
              </Datepicker>
            </v-col>
          </v-row>
          <v-row class="d-flex align-center">
            <v-col>
              <div>Length:</div>
              <v-select
                :items="subscriptionLengths"
                variant="outlined"
                density="compact"
                item-title="label"
                item-value="value"
                hide-details
                @update:modelValue="onSubscriptionLengthChange"
                v-model="editableSubscription.subscriptionLength">
              </v-select>
            </v-col>
            <v-col>
              <div>Next Payment Date:</div>
              <Datepicker
                v-model="editableSubscription.customer.expirationDate"
                :enable-time-picker="false"
                auto-apply
                placeholder="Next Payment Date"
                :format="dateFormat"
                :min-date="new Date()"
                :teleport="true"
                :clearable="false">
              </Datepicker>
            </v-col>
          </v-row>
          <v-row>
            <v-col>
              <v-switch
                color="#E2215A"
                v-model="editableSubscription.customer.subscriptionTypeName"
                true-value="RECURRING"
                false-value="ONE_OFF"
                :label="editableSubscription.customer.subscriptionTypeName"></v-switch>
            </v-col>
            <v-col>
              <v-switch
                color="#E2215A"
                v-model="editableSubscription.customer.status"
                :true-value="1"
                :false-value="0"
                :label="`${
                  Number(editableSubscription.customer.status) ? 'Active' : 'Inactive'
                }`"></v-switch>
            </v-col>
            <v-col>
              <v-switch
                color="#E2215A"
                v-model="editableSubscription.customer.commercial"
                :true-value="1"
                :false-value="0"
                :label="`${
                  Number(editableSubscription.customer.commercial) ? 'Commercial' : 'Personal'
                }`"></v-switch>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12">
              <v-textarea
                clearable
                variant="outlined"
                v-model="editableSubscription.customer.subscriptionNotes"
                label="Notes"
                persistent-placeholder
                required></v-textarea>
            </v-col>
          </v-row>
        </v-form>
      </v-card-text>
      <v-card-actions>
        <!-- Restart EWAY Rebill hidden — EWAY SOAP API cannot create new rebills with masked CC data -->
        <!--
        <v-btn
          v-if="showRestartEwayRebill"
          color="orange"
          variant="flat"
          class="text-white"
          @click="showRestartConfirm = true"
          >Restart EWAY Rebill</v-btn
        >
        <div v-if="restartEwayRebillMessage" class="ml-2 text-caption">
          {{ restartEwayRebillMessage }}
        </div>

        <v-dialog v-model="showRestartConfirm" width="400">
          <v-card>
            <v-card-title>Restart EWAY Rebill</v-card-title>
            <v-card-text>
              <p class="mb-4">
                This will restart the EWAY recurring billing for this subscription from today.
              </p>
              <div>Next Payment Date:</div>
              <Datepicker
                v-model="restartNextPaymentDate"
                :enable-time-picker="false"
                auto-apply
                placeholder="Next Payment Date"
                :format="dateFormat"
                :min-date="new Date()"
                :teleport="true"
                :clearable="false">
              </Datepicker>
            </v-card-text>
            <v-card-actions>
              <v-spacer></v-spacer>
              <v-btn color="primary" text @click="showRestartConfirm = false">Cancel</v-btn>
              <v-btn color="orange" variant="flat" class="text-white" @click="onRestartEwayRebill"
                >Confirm</v-btn
              >
            </v-card-actions>
          </v-card>
        </v-dialog>
        -->

        <v-spacer></v-spacer>
        <v-btn color="primary" text @click="emit('onClose')">Cancel</v-btn>
        <v-btn
          class="text-white"
          width="100"
          color="#E2215A"
          variant="flat"
          @click="onSaveSubscription"
          >Save</v-btn
        >
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>
